import React, { useEffect, useRef, useState } from "react";
import axios, { AxiosResponse } from "axios";
import { DataTableWithoutButtons } from "components/DataTable/DataTableWithoutButtons";
import { AfDataType } from "utils/TableColumnData";
import handleWheel from "utils/handleOnWheel";
import { ListCommanWay, UpdateCommanWay, CreateCommanWay, PostFormData } from "API/commonApi";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { Img } from "components";
import moment from "moment";
import Swal from "sweetalert2";
import { useAuthStore } from "store/auth";
import UseToast from "utils/AfToast";
import Select from "react-select";
import { GST_OPTIONS, roles } from "utils/constant";
import { utils, write, writeFile } from "xlsx";
import { saveAs } from "file-saver";
import {
  ArrowDownIcon,
  ArrowsUpDownIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { get_file_url } from "utils/lib-utils";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import { Button } from "@heroui/react";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";

// Modern select styles matching other pages
const expenseSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "0px 2px",
    minHeight: "32px",
    fontSize: "13px",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#0077B6" },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 6px",
  }),
  input: (base: any) => ({
    ...base,
    margin: "0px",
    padding: "0px",
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: "32px",
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b",
    fontSize: "13px",
    padding: "6px 10px",
    cursor: "pointer",
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
    zIndex: 9999,
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#94a3b8",
    fontSize: "13px",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#1e293b",
    fontSize: "13px",
  }),
};

const ExpenseVouchureList: React.FC<any> = (props) => {
  const ADT = AfDataType;
  const [tableData, setTableData] = useState<any>([]);
  const [agentId, setAgentId] = useState("");
  const [startDate, setStartDate] = useState<any>(moment().startOf("day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<any>(moment().endOf("day").format("YYYY-MM-DD"));
  const [query, setQuery] = useState<any>({
    date: {
      $gte: moment().startOf("day"),
      $lte: moment().endOf("day"),
    },
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [fetchedData, setFetchedData] = useState([]);
  const [isDataAdded, setIsDataAdded] = useState(false);
  const [sortStat, setSortStat] = useState<any>({});

  const [selectedAgent, setSelectedAgent] = useState<any>([]);
  const [allAgents, setAllAgents] = useState([]);

  const [selectedSubbranch, setSelectedSubbranch] = useState<any>(null);
  const [subbranchOptions, setSubBranchOptions] = useState<any>([]);

  const [selectedVouchers, setSelectedVouchers] = useState<any>([]);
  const [selectedTotal, setSelectedTotal] = useState<any>(0);
  const [firstVoucherStatus, setFirstVoucherStatus] = useState<string>("");

  const [approvedCountMap, setApprovedCountMap] = useState<any>({});
  const [showDisbursePopup, setShowDisbursePopup] = useState(false);
  const [loadAllImages, setLoadAllImages] = useState<boolean>(false);
  const [loadedImageRows, setLoadedImageRows] = useState<Set<number | string>>(new Set());

  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [addExpenseForm, setAddExpenseForm] = useState<any>({
    head: "",
    remark: "",
    expense: "",
    date: moment().format("YYYY-MM-DD"),
    refNo: "",
  });
  const [addExpenseImages, setAddExpenseImages] = useState<File[]>([]);
  const [addExpenseLoading, setAddExpenseLoading] = useState(false);

  const expenseHeadOptions = [
    { value: "FUEL", label: "Fuel" },
    { value: "TRAVEL", label: "Travel" },
    { value: "FOOD", label: "Food" },
    { value: "TOLL", label: "Toll" },
    { value: "STATIONARY", label: "Stationary" },
    { value: "MOBILE", label: "Mobile" },
    { value: "REPAIR", label: "Repair" },
    { value: "OTHER", label: "Other" },
  ];

  const handleAddExpense = async () => {
    if (!addExpenseForm.head || !addExpenseForm.expense) {
      UseToast("Please fill Head and Amount", "error");
      return;
    }
    setAddExpenseLoading(true);
    try {
      let uploadedUrl: string[] = [];
      for (let i = 0; i < addExpenseImages.length; i++) {
        const formData = new FormData();
        formData.append("files", addExpenseImages[i]);
        const fileRes: any = await PostFormData("/admin/upload-to-azure", formData);
        if (fileRes.status === "SUCCESS" && fileRes?.data?.uploadSuccess?.length === 1) {
          uploadedUrl.push(fileRes.data.uploadSuccess[0].path);
        }
      }
      const saveObj: any = {
        head: addExpenseForm.head,
        remark: addExpenseForm.remark,
        expense: addExpenseForm.expense.trim().replaceAll(",", ""),
        date: addExpenseForm.date,
        image: uploadedUrl.join(","),
      };
      if (addExpenseForm.refNo.trim()) saveObj.refNo = addExpenseForm.refNo.trim();

      const res: any = await CreateCommanWay({ url: "/admin/expense_vouchure/create", data: saveObj });
      if (res?.status === "SUCCESS") {
        UseToast("Expense added successfully!", "success");
        setShowAddExpenseModal(false);
        setAddExpenseForm({ head: "", remark: "", expense: "", date: moment().format("YYYY-MM-DD"), refNo: "" });
        setAddExpenseImages([]);
        refetch();
      } else {
        UseToast(res?.message || "Failed to add expense", "error");
      }
    } catch (err: any) {
      UseToast(typeof err === "string" ? err : err?.message || "Failed to add expense", "error");
    }
    setAddExpenseLoading(false);
  };

  const [selectedStatus, setSelectedStatus] = useState({ value: "", label: "--- Select All ---" });
  const [allStatusList, setAllStatusList] = useState([
    { value: "", label: "--- Select All ---" },
    { value: "PENDING", label: "Pending" },
    { value: "APPROVED", label: "Approved" },
    { value: "DISBURSED", label: "Disbursed" },
    { value: "REJECTED", label: "Rejected" },
  ]);

  const [selectedGstType, setSelectedGstType] = useState<{ value: string; label: string }>({
    value: "",
    label: "All",
  });
  const allGstTypeList = [
    { value: "", label: "All" },
    { value: "NON_GST", label: "Non-GST" },
    { value: "GST_ONLY", label: "Gst Only" },
  ];

  const [selectedCompanyGst, setSelectedCompanyGst] = useState<any>(null);
  const allCompanyGstList = [
    { value: "", label: "--- Select All ---" },
    ...GST_OPTIONS.map((g) => ({ value: g.value, label: g.label })),
  ];

  const [customerGstInput, setCustomerGstInput] = useState<string>("");
  const [minExpense, setMinExpense] = useState<string>("");
  const [maxExpense, setMaxExpense] = useState<string>("");

  useEffect(() => {
    let total = 0;
    let status = "";
    if (tableData.length) {
      tableData.forEach((t) => {
        if (selectedVouchers.includes(t.id)) {
          total += Number(t.expense);
          status = t.status;
        }
      });
      setSelectedTotal(total);
      setFirstVoucherStatus(status);
    }
  }, [selectedVouchers]);

  useQuery(["SubBranchesListApi_expense"], () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        let opt = data.data.data.map((p) => ({ value: p.id, label: p.branch_name }));
        setSubBranchOptions(opt);
      }
    },
  });

  useEffect(() => {
    const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
    if (Object.keys(userD).length) {
      setUserDetails(userD.state.user);
      console.log("userD", userD.state.user);
      if (userD.state.user.username == "KIRANG") {
        mutateListAgents({ subbranch: [43, 46, 53, 55, 54, 56] });
      } else if (userD.state.user.username == "sarvanaa") {
        mutateListAgents({ subbranch: [53, 55] });
      } else if (userD.state.user.roleName == roles.SUPER_ADMIN || userD.state.user.roleName == roles.CA_READ_ONLY) {
        mutateListAgents({ subbranch: undefined });
      } else if (userD.state.user.roleName == roles.SUBBRANCH_ADMIN) {
        mutateListAgents({ subbranch: userD.state.user?.subbranch });
      } else {
        mutateListAgents({ subbranch: undefined });
      }
    }
  }, []);

  const { isFetching, isLoading, refetch } = useQuery(
    ["admin_expense_vouchure_list"],
    () =>
      ListCommanWay("/admin/expense_vouchure/list", query, {
        include: ["_user"],
      }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          console.log("data.data", data.data);
          setTableData(data.data.data);
          setApprovedCountMap(data.data.approvedCountMap || {});
          setSortStat({});
          setSelectedVouchers([]);
          setSelectedTotal(0);
          setFirstVoucherStatus("");
        } else {
          setTableData([]);
          setApprovedCountMap({});
        }
      },
      onError: (data) => {
        setTableData([]);
      },
    },
  );

  const { mutate: mutateListAgents }: any = useMutation(
    (qry: any) =>
      ListCommanWay(
        "/admin/user/list",
        {
          subbranch: qry?.subbranch,
          isActive: true,
        },
        { paginate: 100000 },
      ),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          setAllAgents([{ id: null, name: "--- Select All ---" }, ...data.data.data]);
        }
      },
      onError: (data: any) => {},
    },
  );

  const {
    mutate,
    isLoading: isloadingPrice,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => UpdateCommanWay(data), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        refetch();
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });
  const onStateChange = (id: any, status) => {
    Swal.fire({
      title: "Are you sure!",
      text: "Changing Status to : " + status,
      icon: "info",
      confirmButtonText: "Yes",
      showCancelButton: true,
      html:
        '<input id="user_remark" class="swal2-input" placeholder="Enter Remark">' +
        '<input id="transactionNo" class="swal2-input" placeholder="Transaction No">',
    }).then((x) => {
      if (x.isConfirmed) {
        let updateData: any = {
          account_remark: undefined,
          transactionNo: (document.getElementById("transactionNo") as any).value,
          account_date: undefined,
          status: status,
        };
        if (userDetails.role_name == 9 || userDetails.role_name == 3 || ["KIRANG", "sarvanaa"].includes(userDetails.username)) {
          updateData.admin_remark = (document.getElementById("user_remark") as any).value;
          updateData.admin_date = new Date();
        } else {
          updateData.account_remark = (document.getElementById("user_remark") as any).value;
          updateData.account_date = new Date();
          if (updateData.transactionNo == "") {
            delete updateData.transactionNo;
          }
        }
        if (typeof id == "object") {
          mutate({
            url: "/admin/expense_vouchure/updateBulk",
            data: {
              filter: { id: selectedVouchers },
              data: updateData,
            },
          });
        } else {
          mutate({
            url: "/admin/expense_vouchure/update/" + id,
            data: updateData,
          });
        }
      }
    });
  };

  const onGstInfoChange = (id: any) => {
    Swal.fire({
      title: "Add GST Details!",
      icon: "info",
      confirmButtonText: "Yes",
      showCancelButton: true,
      html:
        //inputs for cgst, sgst, igst
        '<input style="height: 1.5em; margin: 4px;" id="cgst" class="swal2-input" placeholder="CGST (%)">' +
        '<input style="height: 1.5em; margin: 4px;" id="sgst" class="swal2-input" placeholder="SGST (%)">' +
        '<input style="height: 1.5em; margin: 4px;" id="igst" class="swal2-input" placeholder="IGST (%)">' +
        '<input style="height: 1.5em; margin: 4px;" id="customerGstNo" class="swal2-input" placeholder="Customer GST No">' +
        '<input style="height: 1.5em; margin: 4px;" id="airfieldGstNo" class="swal2-input" placeholder="Airfield GST NO">',
    }).then((x) => {
      if (x.isConfirmed) {
        let updateData: any = {
          cgst: Number((document.getElementById("cgst") as any).value) || 0,
          sgst: Number((document.getElementById("sgst") as any).value) || 0,
          igst: Number((document.getElementById("igst") as any).value) || 0,
          customerGstNo: (document.getElementById("customerGstNo") as any).value || "",
          airfieldGstNo: (document.getElementById("airfieldGstNo") as any).value || "",
        };
        if (typeof id == "object") {
          mutate({
            url: "/admin/expense_vouchure/updateBulk",
            data: {
              filter: { id: selectedVouchers },
              data: updateData,
            },
          });
        } else {
          mutate({
            url: "/admin/expense_vouchure/update/" + id,
            data: updateData,
          });
        }
      }
    });
  };

  useEffect(() => {
    refetch();
  }, [query]);

  const handleSubmit = async () => {
    setIsDataAdded(!isDataAdded);

    let userFilter: any = undefined;
    if (selectedAgent.length > 0) {
      const ids = selectedAgent.map((a: any) => a.id);
      userFilter = ids.length === 1 ? ids[0] : ids;
    } else if (selectedSubbranch && allAgents.length > 1) {
      // When subbranch is selected but no specific employee, pass all user ids from that subbranch
      const userIds = allAgents.filter((a: any) => a.id !== null).map((a: any) => a.id);
      if (userIds.length > 0) userFilter = userIds;
    }

    let customerGstFilter: any = undefined;
    if (customerGstInput.trim()) {
      customerGstFilter = { $like: `${customerGstInput.trim()}%` };
    } else if (selectedGstType?.value === "NON_GST") {
      customerGstFilter = null;
    } else if (selectedGstType?.value === "GST_ONLY") {
      customerGstFilter = { $ne: null };
    }

    const minVal = minExpense !== "" && !isNaN(Number(minExpense)) ? Number(minExpense) : null;
    const rawMaxVal = maxExpense !== "" && !isNaN(Number(maxExpense)) ? Number(maxExpense) : null;

    let expenseFilter: any = undefined;
    if (minVal !== null && rawMaxVal !== null) {
      const maxVal = Number.isInteger(rawMaxVal) ? rawMaxVal + 1 : rawMaxVal + 0.01;
      expenseFilter = {
        $gte: minVal,
        $lt: maxVal,
      };
    } else if (minVal !== null) {
      const computedMax = Number.isInteger(minVal) ? minVal + 1 : minVal + 0.01;
      expenseFilter = {
        $gte: minVal,
        $lt: computedMax,
      };
    } else if (rawMaxVal !== null) {
      const maxVal = Number.isInteger(rawMaxVal) ? rawMaxVal + 1 : rawMaxVal + 0.01;
      expenseFilter = {
        $lt: maxVal,
      };
    }

    setQuery({
      //agent_id: Number(agentId),
      date: {
        $gte: startDate ? startDate : moment().startOf("day").subtract(7, "days"),
        $lte: endDate ? moment(endDate).endOf("day") : moment().endOf("day"),
      },
      user: userFilter,
      status: selectedStatus.value == "" ? undefined : selectedStatus.value,
      airfieldGstNo: selectedCompanyGst?.value ? selectedCompanyGst.value : undefined,
      customerGstNo: customerGstFilter,
      expense: expenseFilter,
    });
  };

  const tableRef = useRef(null);
  const exportToExcel = () => {
    const table = tableRef.current;

    const tableClone = table.cloneNode(true);

    const tfoot = tableClone.getElementsByTagName("tfoot");
    if (tfoot.length > 0) {
      tfoot[0].remove();
    }

    const ws = utils.table_to_sheet(tableClone);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Sheet1");

    Object.keys(ws).forEach((key) => {
      if (key.startsWith("!")) return;

      let cell = ws[key];
      if (cell && cell.h && typeof cell.h === "string") {
        cell.h = null;
      }
      if (cell && cell.l) {
        delete cell.l;
      }
    });

    const wbout = write(wb, { bookType: "xlsx", type: "array" });

    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "Expense Vouchure Report.xlsx");
  };

  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
  const [userDetails, setUserDetails] = useState<any>(userD?.state?.user || {});
  const { paymentPublicKey } = useAuthStore();

  const encryptWithRSA = async (data: object): Promise<string | false> => {
    if (!paymentPublicKey) return false;
    try {
      // Clean the key - handle PEM headers, escaped newlines, whitespace
      let pemBody = paymentPublicKey
        .replace(/-----BEGIN (RSA )?PUBLIC KEY-----/g, "")
        .replace(/-----END (RSA )?PUBLIC KEY-----/g, "")
        .replace(/\\n/g, "")
        .replace(/\s/g, "");

      const binaryDer = Uint8Array.from(atob(pemBody), (c) => c.charCodeAt(0));

      const cryptoKey = await crypto.subtle.importKey(
        "spki",
        binaryDer.buffer,
        { name: "RSA-OAEP", hash: "SHA-256" },
        false,
        ["encrypt"],
      );

      const encoded = new TextEncoder().encode(JSON.stringify(data));
      console.log("Encoded data for encryption:", encoded);
      const encrypted = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, cryptoKey, encoded);

      // Convert to base64 safely (avoid stack overflow for large arrays)
      const bytes = new Uint8Array(encrypted);
      let binary = "";
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return btoa(binary);
    } catch (e) {
      console.error("RSA-OAEP encryption failed:", e);
      return false;
    }
  };

  const handleBulkPayment = async () => {
    if (!paymentPublicKey) {
      UseToast("Payment key not available. Please re-login.", "error");
      return;
    }
    if (selectedVouchers.length === 0) {
      UseToast("Please select vouchers first.", "error");
      return;
    }

    // Encrypt payment request (backend will self-identify branch for expense vouchers)
    const encryptedRequest = await encryptWithRSA({
      paymentType: "EXPENSE_VOUCHER",
    });
    if (!encryptedRequest) {
      UseToast("Encryption failed. Please re-login.", "error");
      return;
    }

    try {
      const otpRes: any = await CreateCommanWay({
        url: "/admin/payment/request-otp",
        data: { payload: encryptedRequest, ids: selectedVouchers },
      });

      if (otpRes?.status !== "SUCCESS") {
        UseToast(otpRes?.message || "Failed to request OTP", "error");
        return;
      }

      const requestId = otpRes?.data?.requestId;

      // Step 3: Ask for OTP
      const { value: otp } = await Swal.fire({
        title: "Enter OTP",
        input: "text",
        inputPlaceholder: "Enter 6-digit OTP",
        showCancelButton: true,
        confirmButtonText: "Release Payment",
        inputValidator: (value) => {
          if (!value) return "Please enter OTP";
          if (value.length !== 6) return "OTP must be 6 digits";
        },
      });

      if (!otp) return;

      // Step 4: Encrypt OTP payload and call release
      const releasePayload = { requestId, otp };
      const encryptedRelease = await encryptWithRSA(releasePayload);
      if (!encryptedRelease) {
        UseToast("Encryption failed.", "error");
        return;
      }

      const releaseRes: any = await CreateCommanWay({
        url: "/admin/payment/release",
        data: { payload: encryptedRelease },
      });

      if (releaseRes?.status === "SUCCESS") {
        UseToast("Payment released successfully!", "success");
        refetch();
      } else {
        UseToast(releaseRes?.message || "Payment release failed", "error");
      }
    } catch (err: any) {
      UseToast(typeof err === "string" ? err : err?.message || "Something went wrong", "error");
    }
  };

  const GET_SORT_ICON = (key, info) => {
    switch (sortStat[key]) {
      case "asc":
        return (
          <ArrowDownIcon
            className="h-4 inline-block"
            onClick={() => {
              handleSort(key, info);
            }}
          />
        );
      case "desc":
        return (
          <ArrowUpIcon
            className="h-4 inline-block"
            onClick={() => {
              handleSort(key, info);
            }}
          />
        );
      default:
        return (
          <ArrowsUpDownIcon
            className="h-4 inline-block"
            onClick={() => {
              handleSort(key, info);
            }}
          />
        );
    }
  };

  const handleSort = (key, info) => {
    let data = [...tableData];
    let order = sortStat[key] == "asc" ? "desc" : "asc";

    if (key == "_invoice_no_c_b.isFullPaid") {
      data = data.sort((a, b) => {
        if (order == "asc") {
          return -a["isActive"] + b["isActive"];
        } else {
          return -b["isActive"] + a["isActive"];
        }
      });
    }

    let sortedData = data.sort((a, b) => {
      const valueA = key.split(".").reduce((obj, key) => obj?.[key], a);
      const valueB = key.split(".").reduce((obj, key) => obj?.[key], b);

      const numA = Number(valueA);
      const numB = Number(valueB);
      const isNumericA = valueA !== "" && valueA !== null && valueA !== undefined && !Number.isNaN(numA);
      const isNumericB = valueB !== "" && valueB !== null && valueB !== undefined && !Number.isNaN(numB);

      if (isNumericA && isNumericB) {
        return order === "asc" ? numA - numB : numB - numA;
      }

      const strA = String(valueA ?? "").toLowerCase();
      const strB = String(valueB ?? "").toLowerCase();
      if (strA < strB) return order === "asc" ? -1 : 1;
      if (strA > strB) return order === "asc" ? 1 : -1;
      return 0;
    });
    let _sortStat = { ...sortStat };
    _sortStat[key] = order;
    setSortStat(_sortStat);
    setTableData(sortedData);
  };

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter flex-1">
      {/* ═══════════ HEADER BAR ═══════════ */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-center gap-3">
            <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">
              Expense Voucher
            </h1>
          </div>
        </div>
      </div>

      {/* ═══════════ FILTERS CARD ═══════════ */}
      <div className="mx-auto mb-2">
        <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
          <div className="px-3 py-2">
            <div className="flex gap-3 items-center flex-wrap">
              {(userDetails.roleName == roles.SUPER_ADMIN || userDetails.roleName == roles.CA_READ_ONLY || userDetails.roleName == roles.MANAGER) && (
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-medium text-slate-500">Subbranch</label>
                  <Select
                    className="min-w-[180px]"
                    styles={expenseSelectStyles}
                    options={subbranchOptions}
                    onChange={(e: any) => {
                      setSelectedSubbranch(e);
                      setSelectedAgent([]);
                      mutateListAgents({ subbranch: e?.value || undefined });
                    }}
                    value={selectedSubbranch}
                    isClearable
                    placeholder="All Subbranches"
                    menuPortalTarget={document.body}
                  />
                </div>
              )}

              {([1, 2, 3, 5, 9, 13].includes(userDetails.role_name) || ["KIRANG", "sarvanaa"].includes(userDetails.username)) && (
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-medium text-slate-500">Employee</label>
                  <Select
                    className="min-w-[250px]"
                    styles={expenseSelectStyles}
                    options={allAgents.filter((a: any) => a.id !== null)}
                    onChange={(e: any) => setSelectedAgent(e || [])}
                    value={selectedAgent}
                    getOptionValue={(e) => e.id + ""}
                    getOptionLabel={(e) => e.name}
                    isMulti
                    isClearable
                    placeholder="All Employees"
                    menuPortalTarget={document.body}
                  />
                </div>
              )}

              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-slate-500">Status</label>
                <Select
                  className="min-w-[100px]"
                  styles={expenseSelectStyles}
                  options={allStatusList}
                  onChange={(e: any) => setSelectedStatus(e)}
                  value={selectedStatus}
                  menuPortalTarget={document.body}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-slate-500">GST Type</label>
                <Select
                  className="min-w-[130px]"
                  styles={expenseSelectStyles}
                  options={allGstTypeList}
                  onChange={(e: any) => setSelectedGstType(e || { value: "", label: "All" })}
                  value={selectedGstType}
                  menuPortalTarget={document.body}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-slate-500">Company GST</label>
                <Select
                  className="min-w-[200px]"
                  styles={expenseSelectStyles}
                  options={allCompanyGstList}
                  onChange={(e: any) => setSelectedCompanyGst(e)}
                  value={selectedCompanyGst}
                  isClearable
                  placeholder="All Company GST"
                  menuPortalTarget={document.body}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-slate-500">Customer GST No</label>
                <input
                  type="text"
                  value={customerGstInput}
                  onChange={(e) => setCustomerGstInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSubmit();
                  }}
                  placeholder="Customer GST No"
                  className="h-8 px-2.5 text-[13px] bg-white/90 border border-slate-200 rounded-[10px] focus:outline-none focus:border-[#0077B6] focus:ring-2 focus:ring-[#0077B6]/20 transition-all min-w-[120px]"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-slate-500">Amount (Min - Max)</label>
                <div className="flex items-center">
                  <input
                    type="number"
                    value={minExpense}
                    onChange={(e) => setMinExpense(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSubmit();
                    }}
                    placeholder="Min"
                    className="h-8 w-16 sm:w-20 px-2 text-[13px] bg-white/90 border border-slate-200 rounded-l-[10px] border-r-0 focus:outline-none focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6]/20 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <span className="h-8 px-1.5 bg-slate-100 border-y border-slate-200 text-slate-400 text-xs flex items-center justify-center select-none">
                    -
                  </span>
                  <input
                    type="number"
                    value={maxExpense}
                    onChange={(e) => setMaxExpense(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSubmit();
                    }}
                    placeholder="Max"
                    className="h-8 w-16 sm:w-20 px-2 text-[13px] bg-white/90 border border-slate-200 rounded-r-[10px] focus:outline-none focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6]/20 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              </div>

              <div className="self-start">
                <AfDateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  label="From - To Date"
                  className="bg-white"
                />
              </div>

              <Button size="sm" color="success" variant="solid" className="h-8 text-xs font-semibold text-white" onPress={handleSubmit}>
                Search
              </Button>
              <Button size="sm" color="primary" variant="flat" className="h-8 text-xs font-semibold" onPress={exportToExcel}>
                Excel Export
              </Button>
              <Button size="sm" color="secondary" variant="solid" className="h-8 text-xs font-semibold text-white" onPress={() => setShowAddExpenseModal(true)}>
                <PlusIcon className="h-4 w-4 mr-1" /> Add Expense
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ BULK ACTIONS CARD ═══════════ */}
      {selectedTotal >= 0 && (
        <div className="mx-auto mb-2">
          <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl px-3 py-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <span className="text-sm font-bold text-slate-800">
                  Selected Total: <span className="text-lg text-primary2">{selectedTotal}</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                {Object.keys(approvedCountMap).length > 0 && (
                  <Button
                    size="sm"
                    color="secondary"
                    variant="solid"
                    className="h-7 text-xs font-semibold text-white"
                    onPress={() => setShowDisbursePopup(true)}
                  >
                    For Disburse (₹{(Object.values(approvedCountMap).reduce((sum: number, item: any) => sum + item.total_amount, 0) as number).toLocaleString('en-IN')})
                  </Button>
                )}
                <Button
                  size="sm"
                  color="success"
                  variant="solid"
                  className="h-7 text-xs font-semibold text-white"
                  isDisabled={selectedTotal == 0}
                  onPress={() => {
                    onStateChange(selectedVouchers, userDetails.role_name == 9 || userDetails.role_name == 3 ? "APPROVED" : "DISBURSED");
                  }}
                >
                  {userDetails.role_name == 9 || userDetails.role_name == 3 ? "Bulk Approve" : "Bulk Disbursed"}
                </Button>
                <Button
                  size="sm"
                  color="danger"
                  variant="solid"
                  className="h-7 text-xs font-semibold"
                  isDisabled={selectedTotal == 0}
                  onPress={() => {
                    onStateChange(selectedVouchers, "REJECTED");
                  }}
                >
                  Bulk Reject
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  variant="bordered"
                  className="h-7 text-xs font-semibold"
                  onPress={() => {
                    onGstInfoChange(selectedVouchers);
                  }}
                >
                  Add GST Info
                </Button>
                {userDetails.role_name == 1 && paymentPublicKey && (
                  <Button
                    size="sm"
                    color="warning"
                    variant="solid"
                    className="h-7 text-xs font-semibold text-white"
                    isDisabled={selectedTotal == 0}
                    onPress={handleBulkPayment}
                  >
                    Bulk Payment
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ TABLE CARD ═══════════ */}
      <div className="mx-auto">
        <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
          <div className="overflow-x-auto">
            <table className="w-full font-inter text-sm" id="myTable" ref={tableRef}>
              <thead>
                <tr className="bg-gradient-to-r from-slate-50 to-slate-100">
                  {([1, 2, 3, 5, 9].includes(userDetails.role_name) || ["KIRANG", "sarvanaa"].includes(userDetails.username)) && (
                    <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-slate-300 text-primary2 focus:ring-primary2/20 mr-1"
                        onChange={(ip) => {
                          let checked_ = ip.target.checked;
                          if (checked_ && tableData.length) {
                            let firstStatus = tableData[0].status;
                            let obj = [];
                            tableData.forEach((x) => {
                              if (x.status == firstStatus) {
                                obj.push(x.id);
                              }
                            });
                            setSelectedVouchers(obj);
                          } else {
                            setSelectedVouchers([]);
                          }
                        }}
                        checked={selectedVouchers.length}
                      />
                    </th>
                  )}
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                    Txn No
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                    User {GET_SORT_ICON("_user.name", "asc")}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                    Head
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                    Ref No
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                    Amount {GET_SORT_ICON("expense", "")}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider w-28">
                    Expense Date {GET_SORT_ICON("date", "")}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider w-28">
                    Submit Date {GET_SORT_ICON("createdAt", "")}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex flex-col items-center gap-0.5">
                      <span>Image</span>
                      <button
                        type="button"
                        onClick={() => setLoadAllImages(true)}
                        className="normal-case text-[10px] bg-primary2/10 hover:bg-primary2/20 text-primary2 font-semibold px-2 py-0.5 rounded border border-primary2/30 transition-colors cursor-pointer"
                      >
                        Load All
                      </button>
                    </div>
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                    Status {GET_SORT_ICON("status", "")}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                    Manager Remark
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                    Account Remark
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                    GST Info
                  </th>
                  {[1, 2, 3, 5, 9].includes(userDetails.role_name) && (
                    <th className="border-b border-slate-200 px-3 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider w-24">
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {tableData.map((r) => {
                  const statusColor =
                    r.status === "APPROVED"
                      ? "bg-blue-50 border-l-2 border-l-blue-400"
                      : r.status === "REJECTED"
                        ? "bg-red-50 border-l-2 border-l-red-400"
                        : r.status === "DISBURSED"
                          ? "bg-green-50 border-l-2 border-l-green-400"
                          : "bg-white border-l-2 border-l-amber-400";
                  return (
                    <tr
                      key={r.id}
                      className={`${
                        selectedVouchers.includes(r.id) ? "bg-primary2/10" : statusColor
                      } hover:bg-slate-50/80 transition-colors`}
                    >
                      {([1, 2, 3, 5, 9].includes(userDetails.role_name) || ["KIRANG", "sarvanaa"].includes(userDetails.username)) && (
                        <td className="border-b border-slate-100 px-3 py-1.5">
                          {r.status != "REJECTED" &&
                          (selectedVouchers.length == 0 || (selectedVouchers.length && firstVoucherStatus == r.status)) ? (
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-slate-300 text-primary2 focus:ring-primary2/20"
                              onChange={(ip) => {
                                if (ip.target.checked) {
                                  let obj = [...selectedVouchers];
                                  obj.push(r.id);
                                  setSelectedVouchers(obj);
                                } else {
                                  let obj = [...selectedVouchers];
                                  const index = obj.indexOf(r.id);
                                  if (index > -1) {
                                    obj.splice(index, 1);
                                  }
                                  setSelectedVouchers(obj);
                                }
                              }}
                              checked={selectedVouchers.includes(r.id)}
                            />
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                      )}

                      <td className="border-b border-slate-100 px-3 py-1.5 text-[13px] text-slate-700">{r.transactionNo}</td>
                      <td className="border-b border-slate-100 px-3 py-1.5 text-[13px] font-medium text-slate-800">{r._user?.name}</td>
                      <td className="border-b border-slate-100 px-3 py-1.5 text-[13px] text-slate-600">{r.remark}</td>
                      <td className="border-b border-slate-100 px-3 py-1.5 text-[13px] text-slate-600">{r.refNo}</td>
                      <td className="border-b border-slate-100 px-3 py-1.5 text-[13px] font-semibold text-slate-800">{r.expense}</td>
                      <td className="border-b border-slate-100 px-3 py-1.5 text-[13px] text-slate-600">{r.date}</td>
                      <td className="border-b border-slate-100 px-3 py-1.5 text-[13px] text-slate-600">{r.createdAt}</td>

                      <td className="border-b border-slate-100 px-3 py-1.5">
                        {r.image ? (
                          loadAllImages || loadedImageRows.has(r.id) ? (
                            <PhotoProvider
                              toolbarRender={({ rotate, onRotate }) => {
                                return (
                                  <div className="flex gap-4 bg-gray-200/25 p-2 rounded-md">
                                    <button onClick={() => onRotate(rotate - 90)}>
                                      <ArrowUturnLeftIcon className="PhotoView-Slider__toolbarIcon fill-none h-6 p-0" />
                                    </button>
                                    <button onClick={() => onRotate(rotate + 90)}>
                                      <ArrowUturnRightIcon className="PhotoView-Slider__toolbarIcon fill-none h-6 p-0" />
                                    </button>
                                  </div>
                                );
                              }}
                            >
                              {r.image.split(",").map((i: string) => {
                                return (
                                  <div className="flex mb-1 bg-slate-100 rounded-lg p-1 items-center" key={i}>
                                    <PhotoView src={get_file_url(i)}>
                                      <img
                                        width={60}
                                        className="h-[60px] object-cover rounded-md cursor-pointer hover:opacity-80 transition-opacity"
                                        src={get_file_url(i)}
                                      />
                                    </PhotoView>
                                    <a href={get_file_url(i)} target="_blank" rel="noopener noreferrer">
                                      <ArrowTopRightOnSquareIcon className="h-4 ml-1.5 text-slate-400 hover:text-primary2 transition-colors" />
                                    </a>
                                  </div>
                                );
                              })}
                            </PhotoProvider>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setLoadedImageRows((prev) => new Set(prev).add(r.id));
                              }}
                              className="text-[11px] bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-2 py-1 rounded border border-slate-300 transition-colors whitespace-nowrap"
                            >
                              Load Image
                            </button>
                          )
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                      <td className="border-b border-slate-100 px-3 py-1.5">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            r.status === "APPROVED"
                              ? "bg-blue-100 text-blue-700"
                              : r.status === "REJECTED"
                                ? "bg-red-100 text-red-700"
                                : r.status === "DISBURSED"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="border-b border-slate-100 px-3 py-1.5 text-[12px] text-slate-600">
                        {r.admin_remark}
                        {r.admin_date && (
                          <div className="text-[10px] font-semibold text-slate-400 mt-0.5">
                            {moment(r.admin_date).format("DD-MM-YYYY HH:mm")}
                          </div>
                        )}
                      </td>
                      <td className="border-b border-slate-100 px-3 py-1.5 text-[12px] text-slate-600">
                        {r.account_remark}
                        {r.account_date && (
                          <div className="text-[10px] font-semibold text-slate-400 mt-0.5">
                            {moment(r.account_date).format("DD-MM-YYYY HH:mm")}
                          </div>
                        )}
                      </td>
                      <td className="border-b border-slate-100 px-3 py-1.5">
                        {r.cgst || r.sgst || r.igst ? (
                          <div className="text-[11px] font-semibold text-slate-600 space-y-0.5">
                            <div className="flex gap-2">
                              <span className="text-slate-400">C:</span>
                              {r.cgst || 0}
                              <span className="text-slate-400">S:</span>
                              {r.sgst || 0}
                              <span className="text-slate-400">I:</span>
                              {r.igst || 0}
                            </div>
                            <div className="text-[10px] text-slate-400 border-t border-slate-100 pt-0.5">
                              AF: {r.airfieldGstNo || "-"} | C: {r.customerGstNo || "-"}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-300 text-[12px]">-</span>
                        )}
                      </td>
                      {(["KIRANG", "sarvanaa"].includes(userDetails.username) || [1, 2, 3, 5, 9].includes(userDetails.role_name)) && (
                        <td className="border-b border-slate-100 px-3 py-1.5">
                          <div className="flex flex-col gap-1">
                            {r.status ==
                              (userDetails.role_name == 9 ||
                              userDetails.role_name == 3 ||
                              ["KIRANG", "sarvanaa"].includes(userDetails.username)
                                ? "PENDING"
                                : "APPROVED") && (
                              <Button
                                size="sm"
                                color="success"
                                variant="flat"
                                className="h-6 text-[11px] font-semibold min-w-0"
                                onPress={() => {
                                  onStateChange(
                                    r.id,
                                    userDetails.role_name == 9 ||
                                      userDetails.role_name == 3 ||
                                      ["KIRANG", "sarvanaa"].includes(userDetails.username)
                                      ? "APPROVED"
                                      : "DISBURSED",
                                  );
                                }}
                              >
                                {userDetails.role_name == 9 ||
                                userDetails.role_name == 3 ||
                                ["KIRANG", "sarvanaa"].includes(userDetails.username)
                                  ? "Approve"
                                  : "Disburse"}
                              </Button>
                            )}
                            {r.status != "REJECTED" && (
                              <Button
                                size="sm"
                                color="danger"
                                variant="flat"
                                className="h-6 text-[11px] font-semibold min-w-0"
                                onPress={() => {
                                  onStateChange(r.id, "REJECTED");
                                }}
                              >
                                Reject
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ═══════════ ADD EXPENSE MODAL ═══════════ */}
      {showAddExpenseModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-[9999] pt-20" onClick={() => setShowAddExpenseModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-[500px] max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-green-500 to-teal-500 px-5 py-3 flex items-center justify-between">
              <h2 className="text-white font-bold text-sm">Add New Expense</h2>
              <button className="text-white/80 hover:text-white text-xl font-bold" onClick={() => setShowAddExpenseModal(false)}>×</button>
            </div>
            <div className="overflow-y-auto max-h-[60vh] p-5 space-y-4">
              <div>
                <label className="text-[12px] font-medium text-slate-600 mb-1 block">Expense Head *</label>
                <Select
                  styles={expenseSelectStyles}
                  options={expenseHeadOptions}
                  onChange={(e: any) => setAddExpenseForm({ ...addExpenseForm, head: e?.value || "" })}
                  value={expenseHeadOptions.find((o) => o.value === addExpenseForm.head) || null}
                  placeholder="Select Head"
                  menuPortalTarget={document.body}
                />
              </div>
              <div>
                <label className="text-[12px] font-medium text-slate-600 mb-1 block">Amount *</label>
                <input
                  type="text"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="Enter amount"
                  value={addExpenseForm.expense}
                  onChange={(e) => setAddExpenseForm({ ...addExpenseForm, expense: e.target.value })}
                />
              </div>
              <div>
                <label className="text-[12px] font-medium text-slate-600 mb-1 block">Remark / Description</label>
                <input
                  type="text"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="Enter remark"
                  value={addExpenseForm.remark}
                  onChange={(e) => setAddExpenseForm({ ...addExpenseForm, remark: e.target.value })}
                />
              </div>
              <div>
                <label className="text-[12px] font-medium text-slate-600 mb-1 block">Ref No</label>
                <input
                  type="text"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="Reference number (optional)"
                  value={addExpenseForm.refNo}
                  onChange={(e) => setAddExpenseForm({ ...addExpenseForm, refNo: e.target.value })}
                />
              </div>
              <div>
                <label className="text-[12px] font-medium text-slate-600 mb-1 block">Expense Date</label>
                <input
                  type="date"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  value={addExpenseForm.date}
                  onChange={(e) => setAddExpenseForm({ ...addExpenseForm, date: e.target.value })}
                />
              </div>
              <div>
                <label className="text-[12px] font-medium text-slate-600 mb-1 block">Upload Images</label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm file:mr-3 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
                  onChange={(e) => setAddExpenseImages(Array.from(e.target.files || []))}
                />
                {addExpenseImages.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {addExpenseImages.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img src={URL.createObjectURL(img)} className="w-16 h-16 object-cover rounded-lg border border-slate-200" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <Button size="sm" color="default" variant="flat" className="h-8 text-xs font-semibold" onPress={() => setShowAddExpenseModal(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                color="success"
                variant="solid"
                className="h-8 text-xs font-semibold text-white"
                isLoading={addExpenseLoading}
                onPress={handleAddExpense}
              >
                Submit Expense
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ FOR DISBURSE POPUP ═══════════ */}
      {showDisbursePopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-[9999] pt-20" onClick={() => setShowDisbursePopup(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-[500px] max-h-[70vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-purple-500 to-blue-500 px-5 py-3 flex items-center justify-between">
              <h2 className="text-white font-bold text-sm">For Disburse Summary</h2>
              <button className="text-white/80 hover:text-white text-xl font-bold" onClick={() => setShowDisbursePopup(false)}>×</button>
            </div>
            <div className="overflow-y-auto max-h-[50vh] p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-2 text-[11px] font-semibold text-slate-600 uppercase">Name</th>
                    <th className="text-center py-2 px-2 text-[11px] font-semibold text-slate-600 uppercase">Count</th>
                    <th className="text-right py-2 px-2 text-[11px] font-semibold text-slate-600 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(approvedCountMap).map(([key, item]: [string, any]) => (
                    <tr key={key} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-2 text-[13px] text-slate-700">{item.name}</td>
                      <td className="py-2 px-2 text-[13px] text-slate-700 text-center">{item.approved_count}</td>
                      <td className="py-2 px-2 text-[13px] font-semibold text-slate-800 text-right">₹{item.total_amount.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50">
                    <td className="py-2 px-2 text-[13px] font-bold text-slate-800">Total</td>
                    <td className="py-2 px-2 text-[13px] font-bold text-slate-800 text-center">
                      {Object.values(approvedCountMap).reduce((sum: number, item: any) => sum + item.approved_count, 0) as number}
                    </td>
                    <td className="py-2 px-2 text-[13px] font-bold text-slate-800 text-right">
                      ₹{(Object.values(approvedCountMap).reduce((sum: number, item: any) => sum + item.total_amount, 0) as number).toLocaleString('en-IN')}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
              <Button size="sm" color="default" variant="flat" className="h-7 text-xs font-semibold" onPress={() => setShowDisbursePopup(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseVouchureList;
